import { useCallback, useState } from 'react';

import { delay } from '@repo/utils/delay';

import userApi from '../api/user';
import { PoWChallenge, PoW, PoWChallengeResponse } from '../types';
import { proofOfWorkWorker } from '../workers/workers';

export const useProofOfWork = (): PoW => {
  const MAX_CHALLENGE_SOLUTION_ATTEMPTS = 5;
  const [challenge, setChallenge] = useState<PoWChallenge | null>({
    isEnabled: true,
  });

  const [isChallengeReady, setChallengeReady] = useState<boolean>(false);

  const executeWorker = (
    powChallenge: PoWChallengeResponse
  ): Promise<PoWChallenge | null> => {
    return new Promise((resolve, reject) => {
      const worker = proofOfWorkWorker();
      worker.onmessage = (solution): void => {
        resolve(solution.data);
        worker.terminate();
      };
      worker.onerror = (error): void => {
        reject(error);
        worker.terminate();
      };

      worker.postMessage({ powChallenge });
    });
  };

  const fetchChallenge = useCallback(async () => {
    let challengeResult: PoWChallenge = { isEnabled: false };
    setChallengeReady(false);

    for (
      let attempt = 0;
      attempt < MAX_CHALLENGE_SOLUTION_ATTEMPTS;
      attempt++
    ) {
      try {
        const powChallenge = await userApi.generatePowChallenge();

        if (!powChallenge.success || !powChallenge.nonce) {
          break;
        }
        const solution = await executeWorker(powChallenge);
        if (solution !== null) {
          challengeResult = solution;
          break;
        }
      } catch (error) {
        await delay(1000); // Wait to avoid multiple request/spawning at once
      }
    }
    setChallenge(challengeResult);
    setChallengeReady(true);

    return challengeResult;
  }, []);

  return { challenge, fetchChallenge, isChallengeReady };
};
